import type { Translations } from "./pt";

export const en: Translations = {
  nav: {
    home: "Home",
    transactions: "Transactions",
    charts: "Charts",
    goals: "Goals",
    budgets: "Budgets",
    reports: "Reports",
    family: "Family",
    admin: "Admin",
    darkMode: "Dark mode",
    lightMode: "Light mode",
    settings: "Settings",
    whatsapp: "WhatsApp",
    bills: "Bills",
    adminPanel: "Admin Panel",
    logout: "Logout",
  },

  dashboard: {
    title: "Financial Dashboard",
    monthBalance: "MONTHLY BALANCE",
    income: "INCOME",
    expenses: "EXPENSES",
    savingsRate: "SAVINGS RATE",
    recentTransactions: "Recent Transactions",
    noTransactions: "No transactions this month",
    addFirst: "Add your first transaction",
    incomeVsExpenses: "Income vs Expenses",
    expensesByCategory: "Expenses by Category",
    balanceEvolution: "Balance Evolution",
    noExpenses: "No expenses recorded this month",
    welcome: "Welcome to Bolso!",
    welcomeNew: "New",
    welcomeDesc: "Record your expenses in seconds — via the site, voice, or directly on WhatsApp.",
    byApp: "Via site",
    byAppDesc: "Tap + to add a transaction",
    byVoice: "By voice",
    byVoiceDesc: 'Say "gas 50 dollars" and done',
    byWhatsApp: "Via WhatsApp",
    byWhatsAppDesc: "Send a message or audio without opening the site",
    linkWhatsApp: "Link WhatsApp",
    alreadyKnow: "I already know, close",
    inviteTitle: "Invite someone to try it",
    inviteDescription: "Share Bolso through WhatsApp. Access is free during the trial period.",
    inviteButton: "Share via WhatsApp",
    trialDays: "{days} days left in the free trial",
    trialToday: "Last day of the free trial",
    trialEnded: "Free trial ended",
  },

  transactions: {
    title: "Transactions",
    new: "New Transaction",
    edit: "Edit Transaction",
    delete: "Delete",
    confirmDelete: "Delete transaction?",
    confirmDeleteDesc: "This action cannot be undone.",
    cancel: "Cancel",
    confirm: "Confirm",
    type: "Type",
    income: "Income",
    expense: "Expense",
    transfer: "Transfer",
    amount: "Amount",
    description: "Description",
    category: "Category",
    date: "Date",
    notes: "Notes",
    save: "Save",
    noTransactions: "No transactions found",
    search: "Search transactions...",
    filter: "Filter",
    all: "All",
    receipt: "Receipt",
    aiCategorized: "AI categorized",
    addTransaction: "Add transaction",
  },

  goals: {
    title: "Goals",
    new: "New Goal",
    edit: "Edit Goal",
    delete: "Delete Goal",
    name: "Name",
    targetAmount: "Target Amount",
    currentAmount: "Current Amount",
    deadline: "Deadline",
    icon: "Icon",
    color: "Color",
    linkedCategory: "Linked category (optional)",
    progress: "Progress",
    completed: "Completed",
    active: "Active",
    cancelled: "Cancelled",
    noGoals: "No goals created",
    createFirst: "Create your first financial goal",
    save: "Save",
    cancel: "Cancel",
    of: "of",
    daysLeft: "days left",
    overdue: "Overdue",
    noDeadline: "No deadline set",
  },

  budgets: {
    title: "Budgets",
    new: "New Budget",
    edit: "Edit Budget",
    delete: "Delete",
    category: "Category",
    limit: "Limit",
    spent: "Spent",
    remaining: "Remaining",
    month: "Month",
    year: "Year",
    noBudgets: "No budgets defined",
    createFirst: "Set spending limits by category",
    save: "Save",
    cancel: "Cancel",
    of: "of",
    alert80: "80% of limit reached",
    alert100: "Limit reached!",
    used: "used",
  },

  reports: {
    title: "Reports",
    period: "Period",
    monthly: "Monthly",
    annual: "Annual",
    byCategory: "By Category",
    family: "Family",
    individual: "Individual",
    export: "Export",
    income: "Income",
    expenses: "Expenses",
    balance: "Balance",
    noData: "No data for the selected period",
  },

  family: {
    title: "Family",
    create: "Create Family Group",
    join: "Join a Group",
    inviteCode: "Invite Code",
    members: "Members",
    admin: "Admin",
    member: "Member",
    leave: "Leave Group",
    noFamily: "You are not part of any family group",
    createOrJoin: "Create or join a group to share finances",
    name: "Group Name",
    save: "Save",
    cancel: "Cancel",
    copyCode: "Copy Code",
    codeCopied: "Code copied!",
  },

  settings: {
    title: "Settings",
    profile: "Profile",
    preferences: "Preferences",
    currency: "Currency",
    language: "Language",
    save: "Save",
    saved: "Saved!",
    cancel: "Cancel",
    currencies: {
      BRL: "Brazilian Real (R$)",
      EUR: "Euro (€)",
      USD: "US Dollar ($)",
      GBP: "British Pound (£)",
    },
    languages: {
      pt: "Português",
      en: "English",
      es: "Español",
      it: "Italian",
    },
    categories: {
      title: "My categories",
      description: "Create your own categories to organize expenses and income.",
      expense: "Expenses",
      income: "Income",
      namePlaceholder: "Category name",
      add: "Add category",
      edit: "Edit category",
      save: "Save category",
      cancel: "Cancel",
      delete: "Delete category",
      empty: "No custom categories",
      deleteConfirm: "Delete this category? Existing transactions will be kept.",
      parent: "Parent category",
      noParent: "No parent category",
      subcategory: "Subcategory",
      created: "Category created!",
      updated: "Category updated!",
      deleted: "Category deleted!",
    },
  },

  auth: {
    login: "Login",
    register: "Register",
    email: "Email",
    password: "Password",
    name: "Name",
    confirmPassword: "Confirm Password",
    forgotPassword: "Forgot password",
    loginWithManus: "Login with Manus",
    loginWithGoogle: "Login with Google",
    noAccount: "Don't have an account?",
    hasAccount: "Already have an account?",
    verifyEmail: "Verify your email",
    verifyEmailDesc: "We sent a confirmation link to",
    resendEmail: "Resend email",
    resetPassword: "Reset Password",
    newPassword: "New Password",
    resetSuccess: "Password reset successfully!",
    logout: "Logout",
  },

  whatsapp: {
    title: "WhatsApp Settings",
    linked: "WhatsApp linked",
    notLinked: "WhatsApp not linked",
    link: "Link WhatsApp",
    unlink: "Unlink",
    phone: "Phone number",
    verifyCode: "Verification code",
    step1: "Save the Bolso number",
    step2: 'Send the message "join ago-family"',
    step3: "Generate your linking code",
    step4: "Send the code via WhatsApp",
    generate: "Generate Code",
    copy: "Copy",
    copied: "Copied!",
    commands: "Available commands",
    cmdBalance: '"balance" — view monthly balance',
    cmdSummary: '"summary" — expense summary',
    cmdHelp: '"help" — view all commands',
  },

  common: {
    loading: "Loading...",
    error: "Error",
    success: "Success",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    add: "Add",
    close: "Close",
    back: "Back",
    next: "Next",
    confirm: "Confirm",
    search: "Search",
    filter: "Filter",
    noData: "No data",
    tryAgain: "Try again",
    comingSoon: "Coming soon",
    january: "January",
    february: "February",
    march: "March",
    april: "April",
    may: "May",
    june: "June",
    july: "July",
    august: "August",
    september: "September",
    october: "October",
    november: "November",
    december: "December",
    months: ["January","February","March","April","May","June","July","August","September","October","November","December"],
  },

  voice: {
    title: "Voice Entry",
    hold: "Hold to record",
    release: "Release to process",
    processing: "Processing...",
    confirm: "Confirm transaction",
    retry: "Try again",
    error: "Could not process audio",
    noMic: "Microphone not available",
    speakTransaction: "Say your transaction",
    idleHint: 'Say something like "I spent 50 at the market"',
    recordingHint: "Recording... tap to stop",
    manualEntry: "Enter manually",
    startRecording: "Start recording",
    stopRecording: "Stop recording",
    microphoneDenied: "Microphone permission was denied. Check your browser settings.",
    unsupportedBrowser: "This browser does not provide a compatible audio format. Enter it manually.",
    exampleExpense: '"I spent 45 at the supermarket"',
    exampleIncome: '"I received 3500 in salary"',
    exampleFuel: '"I paid 80 for fuel"',
  },

  bills: {
    title: "Bills",
    payable: "Payable",
    receivable: "Receivable",
    all: "All",
    pending: "Pending",
    paid: "Paid",
    new: "New Bill",
    edit: "Edit Bill",
    delete: "Delete",
    markPaid: "Mark as paid",
    markUnpaid: "Mark as unpaid",
    dueDate: "Due Date",
    amount: "Amount",
    type: "Type",
    recurrence: "Recurrence",
    recurrenceNone: "No recurrence",
    recurrenceWeekly: "Weekly",
    recurrenceMonthly: "Monthly",
    recurrenceYearly: "Yearly",
    notes: "Notes",
    category: "Category",
    noBills: "No bills registered",
    addFirst: "Add your first payable or receivable bill",
    dueSoon: "Upcoming bills",
    overdue: "Overdue",
    dueToday: "Due today",
    dueTomorrow: "Due tomorrow",
    confirmDelete: "Delete bill?",
    confirmDeleteDesc: "This action cannot be undone.",
    titleLabel: "Title",
    titlePlaceholder: "E.g.: Rent, Electricity bill...",
    save: "Save",
    cancel: "Cancel",
  },

  admin: {
    title: "Administration Panel",
    subtitle: "Overview of users and beta settings",
    totalUsers: "Total users",
    activeUsers: "Active (7 days)",
    inactiveUsers: "Inactive",
    totalTransactions: "Total transactions",
    betaExpiry: "Beta period end date",
    noExpiry: "No end date defined.",
    setExpiry: "Set end date",
    saveDate: "Save date",
    registeredUsers: "Registered Users",
    inviteAccess: "Invite-only access",
    inviteMode: "Restricted mode",
    inviteModeDesc: "When active, only authorized emails can create an account.",
    enableRestrict: "Restrict access",
    disableRestrict: "Open access",
    authorizedEmails: "Authorized emails",
    addEmail: "Add email",
    removeEmail: "Remove",
    copyEmails: "Copy all",
    maxSlots: "Slot limit",
    slotsUsed: "slots used",
    back: "← Back",
  },
};
